import { useMobileOptionCardEducation } from 'components/MobileOptionCard/education';
import * as React from 'react';
import { MdInfoOutline } from 'react-icons/md';
import Link from 'components/Link';
import abbreviateNumber from 'utils/shared/number/abbreviateNumber';
import { TICKER_FLOW_PAGE, PRICING_PAGE } from 'constants/pages';
import TickerName from 'components/TickerName';
import { HeatScore } from 'components/Score';
import { CallPutBackgroundMobile, CallPutText, TopPositionText } from 'components/Table';
import OptionFlowTradeSide from 'components/OptionFlowTradeSide/OptionFlowTradeSide';
import OptionFlowTradeRead from 'components/OptionFlowTradeSide/OptionFlowTradeRead';
import { FullTrade } from 'utils/shared/trades/types';
import { getOptionFlowRowSentiment } from 'utils/shared/trades/getOptionFlowRowSentiment';
import { ENABLE_OPTION_FLOW_READ_MODE } from 'utils/shared/trades/optionFlowRead';
import {
  formatDollarValue,
  formatRatioAsWholePercent,
  formatWholePercent,
} from 'utils/shared/trades/formatTradeValue';
import classNames from 'styles/utils/classNames';
import { TbCalendarDollar } from 'react-icons/tb';
// import TooltipComponent from 'components/Tooltip';
import { useEarningsData } from 'hooks/useEarningsQuery';
// import { getEarningsTooltip } from 'utils/shared/earnings';

import { CardWrapper, Background, MutedText } from './styles';

// Exact rendered height of the card content (pt-1 + py-3 padding around the
// 106px two-column body). Must stay in sync with the `h-[134px]` wrapper below
// or the virtualized lists that consume this constant will drift.
export const MOBILE_CARD_PIXEL_HEIGHT = 134;

interface Props {
  trade: FullTrade;
  watchListTickers: string[];
  earningsMap?: any;
  onOpenPopover?: (trade: FullTrade, anchorElement?: HTMLElement) => void;
}

const MobileOptionCard: React.FC<React.PropsWithChildren<Props>> = ({
  trade,
  earningsMap,
  onOpenPopover,
}) => {
  const { enabled: gexEducation, Help: GEXHelp } = useMobileOptionCardEducation();
  const infoIconRef = React.useRef<HTMLButtonElement>(null);
  const sentiment = getOptionFlowRowSentiment(trade);
  const price = formatDollarValue(trade.price, 2);
  const spot = formatDollarValue(trade.spot, 2);
  const strike = formatDollarValue(trade.strike);
  const otm = formatWholePercent(trade.otmPercent);
  const impliedVol = formatRatioAsWholePercent(trade.impliedVol);

  const { hasEarnings } = useEarningsData(earningsMap?.earnings);

  const tickerHasEarnings = hasEarnings(trade?.ticker || '');

  return (
    <div className="relative">
      {trade.isBlur && (
        <div className="absolute bottom-0 top-0 z-10 my-auto flex w-full items-center justify-center gap-1 px-4 py-[0.4rem] text-center text-[.625rem] text-palette-black-7">
          <Link href={PRICING_PAGE} className="!text-palette-black-7 underline">
            Subscribe
          </Link>{' '}
          to see the full feed
        </div>
      )}
      <div
        className={classNames(
          'h-[134px] overflow-hidden pt-1',
          trade.isBlur && 'opacity-50 blur-sm',
        )}
      >
        <CardWrapper isGoldenSweep={trade.isGoldenSweep} isUnusual={trade.isUnusual}>
          <Background
            isGoldenSweep={trade.isGoldenSweep}
            isUnusual={trade.isUnusual}
            className="px-4 py-3"
          >
            <div className="flex w-full">
              <div className="flex w-1/2 flex-col pr-1 text-xs leading-none">
                <div className="mb-2 flex items-center gap-1">
                  <MutedText>
                    {trade.date} {trade.time} {trade.timeSubscript}
                  </MutedText>
                  <button
                    ref={infoIconRef}
                    className="cursor-pointer text-graph-purple transition-colors"
                    onPointerDown={(e) => {
                      if (e.pointerType === 'touch' && onOpenPopover) {
                        e.preventDefault();
                        e.stopPropagation();
                        onOpenPopover(trade, infoIconRef.current || undefined);
                      }
                    }}
                    onClick={(e) => {
                      if (onOpenPopover) {
                        e.preventDefault();
                        e.stopPropagation();
                        onOpenPopover(trade, infoIconRef.current || undefined);
                      }
                    }}
                    aria-label="View trade details"
                  >
                    <MdInfoOutline className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="flex items-center">
                  <div className="relative">
                    {tickerHasEarnings && (
                      <sup className="pointer-events-auto absolute -left-2 -top-1.5 z-60 cursor-pointer">
                        <TbCalendarDollar
                          aria-hidden="true"
                          className="h-3 w-3 rounded-sm bg-palette-black-2 text-graph-purple"
                        />
                      </sup>
                      //   <TooltipComponent
                      //   title={getEarningsTooltip(earningsData, daysToEarnings)}
                      //   position="right"
                      // >
                      //   <sup className="pointer-events-auto absolute -left-2 -top-1.5 cursor-pointer">
                      //     <TbCalendarDollar
                      //       aria-hidden="true"
                      //       className="w-3 h-3 rounded-sm bg-palette-black-2 text-graph-purple"
                      //     />
                      //   </sup>
                      // </TooltipComponent>
                    )}
                    <Link href={`${TICKER_FLOW_PAGE}/${trade.ticker}`}>
                      <TickerName name={trade?.tickerDetails?.name || ''}>
                        <CallPutBackgroundMobile type={trade.cp || ''}>
                          {trade.ticker}
                        </CallPutBackgroundMobile>
                      </TickerName>
                    </Link>
                  </div>
                  <CallPutText className="ml-2 font-bold" type={sentiment}>
                    <GEXHelp id="premium">${abbreviateNumber(trade.premium)}</GEXHelp>
                  </CallPutText>
                </div>
                <div className="mt-2 flex flex-col gap-1.5">
                  <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 leading-none">
                    <GEXHelp
                      id="size"
                      lesson={{
                        title: 'Size @ price',
                        claim: 'Size and price, decoded.',
                        rows: [
                          { label: 'Size', line: 'Contracts on the print' },
                          { label: '@', line: 'Price per share; × 100 per contract' },
                        ],
                        action: 'Big size at a wall is the print to read.',
                        chapter: 'confirmation',
                      }}
                    >
                      <TopPositionText isTopPosition={trade.isTopPosition}>
                        {trade.size?.toLocaleString()} @ {price}
                      </TopPositionText>
                    </GEXHelp>
                    {ENABLE_OPTION_FLOW_READ_MODE && (
                      <OptionFlowTradeRead trade={trade} variant="popover" />
                    )}
                  </div>
                  <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 leading-none">
                    <CallPutText className="inline font-semibold" type={trade.cp || ''}>
                      {trade.cp || 'Call'}
                    </CallPutText>
                    <GEXHelp id="execution">
                      <OptionFlowTradeSide trade={trade} showTooltip={!gexEducation} />
                    </GEXHelp>
                    <span className="text-white">
                      <GEXHelp id="orderType">{trade.orderType || '-'}</GEXHelp>
                    </span>
                  </div>
                  <div className="flex items-center">
                    <div className="mr-4 shrink-0 text-xs">
                      <GEXHelp id="heatScore">Heat Score</GEXHelp>
                    </div>
                    <div className="w-full pt-0.5" style={{ maxWidth: '64px' }}>
                      <HeatScore scoreWidth={trade.scoreWidth} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex w-1/2 flex-col justify-between space-y-1 pl-1 text-xs leading-none">
                <div className="flex items-center justify-between">
                  <div>
                    <GEXHelp id="expiration">Expiration</GEXHelp>
                  </div>
                  <div>{trade.expiryString}</div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <GEXHelp id="strike">Strike</GEXHelp>
                  </div>
                  <div>{strike}</div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <GEXHelp id="printSpot">Spot</GEXHelp>
                  </div>
                  <div>{spot}</div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <GEXHelp id="otm">OTM</GEXHelp>
                  </div>
                  <div>{otm}</div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <GEXHelp id="contractOI">Open Interest</GEXHelp>
                  </div>
                  <div>{trade.openInterest?.toLocaleString()}</div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <GEXHelp id="iv">Implied Vol</GEXHelp>
                  </div>
                  <div>{impliedVol}</div>
                </div>
              </div>
            </div>
          </Background>
        </CardWrapper>
      </div>
    </div>
  );
};

export default MobileOptionCard;
